import api from "@/lib/api"
import { buildQueryParams } from "@/lib/utils/query-params"
import type { Invoice, PaginatedResponse } from "@/types"

/**
 * Overdue slice of the invoices INDEX — dashboard-local fetch helper.
 *
 * `GET /invoices` accepts `?is_overdue=true` (InvoiceController::index, Phase
 * 67 D-17 — `due_date` strictly in the past AND status NOT IN (paid, void)),
 * but neither the shared `InvoiceFilters` type nor `getInvoices()` in
 * lib/invoices-api.ts expose that param. Rather than widen a shared lib for a
 * single widget, this accountant-only helper builds the query itself on top of
 * the same axios client (auth interceptor, base URL and 401 handling included).
 *
 * Sort: `invoice_date` ascending — `due_date` is NOT in the controller's
 * `$allowedSorts`, so the oldest invoices lead the list, which is the closest
 * available proxy for "longest overdue".
 *
 * Returns the raw paginated envelope so callers can read `meta.total` (the
 * full overdue count) alongside the trimmed row slice.
 */
export const getOverdueInvoices = async (
  limit = 5,
): Promise<PaginatedResponse<Invoice>> => {
  const query = buildQueryParams({
    is_overdue: "true",
    page: 1,
    per_page: limit,
    sort: "invoice_date",
    direction: "asc",
  })
  const response = await api.get<PaginatedResponse<Invoice>>(`/invoices?${query}`)
  return response.data
}
